import { toastError, toastSuccess } from "@/components/AppToast";
import { Button } from "@/components/ui/button";
import { DateTimeRow } from "@/components/ui/date-time-row";
import { Label } from "@/components/ui/label";
import { ReasonField } from "@/components/ui/reason-field";
import { SelectRow } from "@/components/ui/select-row";
import { Text } from "@/components/ui/text";
import { Textarea } from "@/components/ui/textarea";
import { joinChildLabels } from "@/features/selectedChild";
import { formatDisplayDate, parseIsoDate } from "@/lib/date";
import { showAlert } from "@/lib/showAlert";
import { log } from "@/logger";
import {
    useCreateLeaveRequestAttachmentMutation,
    useCreateLeaveRequestMutation,
    useGetSchoolYearQuery,
} from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";
import type { Child } from "@spork/s4p-server-sdk";
import { ImperativeRouter, useRouter } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import {
    DaySelectRow,
    ISO_SCHOOL_WEEKDAYS,
} from "@/components/ui/day-select-row";
import { Separator } from "@/components/ui/separator";
import { type TimeFieldProps } from "@/components/ui/time-field";
import { Icon } from "../../components/ui/icon";
import { AttachmentPicker } from "./AttachmentPicker";
import {
    MAX_ATTACHMENT_BYTES,
    readAttachmentAsBase64,
    resolveAttachmentMimeType,
    type AttachmentFile,
} from "./attachmentUtils";
import { resolveLeaveRequestDateTo } from "./leaveRequestDates";
import {
    LeaveAbsenceMode,
    resolveLeaveRequestTimes,
} from "./leaveRequestTimes";
import { isSingleDayLeaveRequestType, type LeaveRequestType } from "./types";

type LeaveRequestFormProps = {
    selectedChildren: Child[];
    /** Prefills start (and end) date when opening from an absence reminder. */
    initialDateFrom?: string;
};

function laterDate(
    left: Date | undefined,
    right: Date | undefined,
): Date | undefined {
    if (!left) {
        return right;
    }

    if (!right) {
        return left;
    }

    return left > right ? left : right;
}

function isIsoDateWithinRange(
    date: string,
    firstDay: string,
    lastDay: string,
): boolean {
    return date >= firstDay && date <= lastDay;
}

function getErrorMessage(error: unknown, fallback: string): string {
    if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string"
    ) {
        return error.message;
    }

    return fallback;
}

function navigateBackOrHome(router: ImperativeRouter) {
    if (router.canGoBack()) {
        router.back();
        return;
    }

    router.replace("/");
}

export function LeaveRequestForm({
    selectedChildren,
    initialDateFrom = "",
}: LeaveRequestFormProps) {
    const { t } = useTranslation();
    const router = useRouter();
    const [createLeaveRequest, { isLoading: isCreating }] =
        useCreateLeaveRequestMutation();
    const [createAttachment, { isLoading: isUploadingAttachments }] =
        useCreateLeaveRequestAttachmentMutation();
    const [reason, setReason] = useState<LeaveRequestType>("illness");
    const [dateFrom, setDateFrom] = useState(initialDateFrom);
    const [dateTo, setDateTo] = useState(initialDateFrom);
    const [absenceMode, setAbsenceMode] = useState<LeaveAbsenceMode>("all");
    const [repeat, setRepeat] = useState<"one-off" | "daily" | "weekly">(
        "one-off",
    );
    const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);
    const [timeFrom, setTimeFrom] = useState("");
    const [timeTo, setTimeTo] = useState("");
    const [additionalInformation, setAdditionalInformation] = useState("");
    const [attachments, setAttachments] = useState<AttachmentFile[]>([]);

    const isSingleDayReason = isSingleDayLeaveRequestType(reason);
    const isExtracurricular = reason === "extracurricular";
    const isRecurring = repeat !== "one-off";
    const isLoading = isCreating || isUploadingAttachments;

    const userId = useMemo(() => {
        for (const child of selectedChildren) {
            if (typeof child.user_id === "number") {
                return child.user_id;
            }
        }
        return undefined;
    }, [selectedChildren]);
    const { data: schoolYear } = useGetSchoolYearQuery(userId ?? 0, {
        skip: userId == null,
    });

    const schoolYearMinimumDate = useMemo(
        () => (schoolYear ? parseIsoDate(schoolYear.first_day) : undefined),
        [schoolYear],
    );
    const schoolYearMaximumDate = useMemo(
        () => (schoolYear ? parseIsoDate(schoolYear.last_day) : undefined),
        [schoolYear],
    );
    const dateFromMinimumDate = schoolYearMinimumDate;
    const dateFromMaximumDate = schoolYearMaximumDate;
    const dateToMinimumDate = laterDate(
        parseIsoDate(dateFrom),
        schoolYearMinimumDate,
    );
    const dateToMaximumDate = schoolYearMaximumDate;

    function handleReasonChange(nextReason: LeaveRequestType) {
        setReason(nextReason);

        if (isSingleDayLeaveRequestType(nextReason) && dateFrom) {
            setDateTo(dateFrom);
        }

        if (nextReason !== "extracurricular") {
            setRepeat("one-off");
            setDaysOfWeek([]);
        }
        if (nextReason === "holiday" && absenceMode === "arrives") {
            setAbsenceMode("all");
        }

        resetAbsence();
    }

    function resetAbsence() {
        setTimeFrom("");
        setTimeTo("");
    }

    function handleRepeatChange(value: string) {
        if (value === "daily") {
            setRepeat("daily");
            setDaysOfWeek([...ISO_SCHOOL_WEEKDAYS]);
            setDateTo("");
        } else if (value === "weekly") {
            setRepeat("weekly");
            setDaysOfWeek([]);
            setDateTo("");
        } else {
            setRepeat("one-off");
            setDaysOfWeek([]);
        }
    }

    function handleAbsenceMode(value: string) {
        const next: LeaveAbsenceMode =
            value === "arrives" || value === "leaves" || value === "part"
                ? value
                : "all";
        setAbsenceMode(next);

        if (next === "all") {
            resetAbsence();
        }

        if (next === "leaves") {
            setTimeTo("");
            setDateTo("");
        }
        if (next === "arrives") {
            setTimeFrom("");
        }
    }

    function toggleWeekday(days: string[]) {
        setDaysOfWeek(days.map((day) => Number(day)).sort((a, b) => a - b));
    }

    function handleDateFromChange(value: string) {
        setDateFrom(value);

        if (isSingleDayReason) {
            setDateTo(value);
            return;
        }

        if (dateTo && dateTo < value) {
            setDateTo("");
            setTimeTo("");
        }
    }

    function handleDateToChange(value: string) {
        setDateTo(value);

        if (!value.trim()) {
            setTimeTo("");
        }
    }

    async function uploadAttachments(leaveRequestId: number) {
        for (const file of attachments) {
            const mimeType = resolveAttachmentMimeType(
                file.mimeType,
                file.name,
            );

            if (!mimeType) {
                throw new Error(t("leave.attachments.unsupportedType"));
            }

            if (file.size > MAX_ATTACHMENT_BYTES) {
                throw new Error(t("leave.attachments.tooLarge"));
            }

            const contentBase64 = await readAttachmentAsBase64(file.uri);
            await createAttachment({
                leave_request_id: leaveRequestId,
                original_filename: file.name,
                mime_type: mimeType,
                content_base64: contentBase64,
                skip_staff_email: true,
            }).unwrap();
        }
    }

    async function handleSubmit() {
        if (selectedChildren.length === 0) {
            toastError(t("leave.errorTitle"), t("ui.selectStudent"));
            return;
        }

        if (!dateFrom.trim()) {
            toastError(t("leave.errorTitle"), t("leave.startDateRequired"));
            return;
        }

        for (const file of attachments) {
            if (!resolveAttachmentMimeType(file.mimeType, file.name)) {
                toastError(
                    t("leave.errorTitle"),
                    t("leave.attachments.unsupportedType"),
                );
                return;
            }

            if (file.size > MAX_ATTACHMENT_BYTES) {
                toastError(
                    t("leave.errorTitle"),
                    t("leave.attachments.tooLarge"),
                );
                return;
            }
        }

        const trimmedDateFrom = dateFrom.trim();
        const resolvedDateTo = resolveLeaveRequestDateTo(
            reason,
            absenceMode,
            trimmedDateFrom,
            dateTo,
            { recurring: isRecurring },
        );

        if (!resolvedDateTo.ok) {
            toastError(t("leave.errorTitle"), t("leave.endDateRequired"));
            return;
        }

        if (isExtracurricular && isRecurring && daysOfWeek.length === 0) {
            showAlert(t("leave.errorTitle"), t("leave.daysRequired"));
            return;
        }

        const trimmedDateTo = resolvedDateTo.dateTo;

        const resolvedTimes = resolveLeaveRequestTimes({
            absenceMode,
            timeFrom: timeFrom.trim(),
            timeTo: timeTo.trim(),
        });

        if (!resolvedTimes.ok) {
            toastError(
                t("leave.errorTitle"),
                t(`leave.${resolvedTimes.error}`),
            );
            return;
        }

        if (
            schoolYear &&
            (!isIsoDateWithinRange(
                trimmedDateFrom,
                schoolYear.first_day,
                schoolYear.last_day,
            ) ||
                (trimmedDateTo != null &&
                    !isIsoDateWithinRange(
                        trimmedDateTo,
                        schoolYear.first_day,
                        schoolYear.last_day,
                    )))
        ) {
            toastError(
                t("leave.errorTitle"),
                t("leave.dateOutsideSchoolYear", {
                    from: formatDisplayDate(schoolYear.first_day),
                    to: formatDisplayDate(schoolYear.last_day),
                }),
            );
            return;
        }

        let created;
        try {
            created = await createLeaveRequest({
                child_id: selectedChildren.map((child) => child.id),
                reason,
                date_from: trimmedDateFrom,
                date_to: trimmedDateTo,
                time_from: resolvedTimes.timeFrom,
                time_to: resolvedTimes.timeTo,
                days_of_week:
                    isExtracurricular && isRecurring ? daysOfWeek : null,
                message: additionalInformation.trim() || null,
                ...(attachments.length > 0
                    ? {
                          attachment_filenames: attachments.map(
                              (file) => file.name,
                          ),
                      }
                    : {}),
            }).unwrap();
        } catch (error) {
            log.error("createLeaveRequest failed", error);
            toastError(
                t("leave.errorTitle"),
                getErrorMessage(error, t("leave.submitFailed")),
            );
            return;
        }

        let attachmentUploadFailed = false;
        if (attachments.length > 0) {
            try {
                for (const leaveRequest of created) {
                    await uploadAttachments(leaveRequest.id);
                }
            } catch (error) {
                log.error("upload leave request attachments failed", error);
                attachmentUploadFailed = true;
            }
        }

        if (attachmentUploadFailed) {
            toastError(
                t("leave.errorTitle"),
                t("leave.attachments.uploadAfterCreateFailed"),
            );
            navigateBackOrHome(router);
            return;
        }

        const childById = new Map(
            selectedChildren.map((child) => [child.id, child]),
        );
        const childrenFor = (requests: typeof created) =>
            requests.flatMap((request) => {
                const child = childById.get(request.child_id);
                return child ? [child] : [];
            });

        const approved = created.filter(
            (request) => request.status === "approved",
        );
        const pending = created.filter(
            (request) => request.status !== "approved",
        );

        if (approved.length > 0 && pending.length > 0) {
            toastSuccess(
                t("leave.submittedTitle"),
                t("leave.submittedMixedBody", {
                    approvedNames: joinChildLabels(childrenFor(approved)),
                    pendingNames: joinChildLabels(childrenFor(pending)),
                }),
            );
        } else if (approved.length > 0) {
            toastSuccess(
                t("leave.autoApprovedTitle"),
                t("leave.autoApprovedBody", {
                    name: joinChildLabels(childrenFor(approved)),
                }),
            );
        } else {
            toastSuccess(
                t("leave.submittedTitle"),
                t("leave.submittedBody", {
                    name: joinChildLabels(childrenFor(pending)),
                }),
            );
        }

        navigateBackOrHome(router);
    }

    const absenceTimesReady =
        absenceMode === "all" ||
        (absenceMode === "arrives" && Boolean(timeTo.trim())) ||
        (absenceMode === "leaves" && Boolean(timeFrom.trim())) ||
        (absenceMode === "part" &&
            Boolean(timeFrom.trim()) &&
            Boolean(timeTo.trim()));
    const canSubmit =
        selectedChildren.length > 0 &&
        Boolean(dateFrom.trim()) &&
        absenceTimesReady &&
        (!isExtracurricular || !isRecurring || daysOfWeek.length > 0);

    const timeFromField: TimeFieldProps = {
        value: timeFrom,
        onChange: setTimeFrom,
        testID: TEST_IDS[TEST_IDS.LEAVE_REQUEST_TIME_FROM_FIELD],
    };
    const timeToField: TimeFieldProps = {
        value: timeTo,
        onChange: setTimeTo,
        testID: TEST_IDS[TEST_IDS.LEAVE_REQUEST_TIME_TO_FIELD],
    };

    const repeatOptions = useMemo(
        () => [
            {
                label: t("ui.oneOff"),
                value: "one-off",
            },
            {
                label: t("ui.daily"),
                value: "daily",
            },
            {
                label: t("ui.recurring"),
                value: "weekly",
            },
        ],
        [t],
    );

    const absenceModeOptions = useMemo(
        () => [
            { label: t("leave.allDay"), value: "all" },
            ...(reason !== "holiday"
                ? [
                      {
                          label: t("leave.arrivesAt"),
                          value: "arrives",
                      },
                  ]
                : []),
            {
                label: t("leave.leavesAt"),
                value: "leaves",
            },
            {
                label: t("leave.partOfDay"),
                value: "part",
            },
        ],
        [t, reason],
    );

    const shouldShowEndDate =
        !isSingleDayReason &&
        absenceMode !== "arrives" &&
        absenceMode !== "leaves" &&
        !isRecurring;
    const shouldShowEndTime = absenceMode === "part";

    return (
        <View className="gap-6">
            <View className="gap-2">
                <Label>{t("leave.reason")}</Label>
                <ReasonField
                    value={reason}
                    onChange={handleReasonChange}
                    placeholder={t("leave.selectReasonPlaceholder")}
                    testID={TEST_IDS[TEST_IDS.LEAVE_REQUEST_REASON_FIELD]}
                />
                <Text variant="muted">
                    {t(`leave.reasonDescriptions.${reason}`)}
                </Text>
            </View>
            <Separator />
            <View className="gap-3">
                <SelectRow
                    label={t("leave.absent")}
                    value={absenceMode}
                    options={absenceModeOptions}
                    onChange={handleAbsenceMode}
                    testID={TEST_IDS[TEST_IDS.LEAVE_REQUEST_ABSENCE_FIELD]}
                />
                <DateTimeRow
                    label={t(
                        shouldShowEndTime
                            ? "leave.partOfDayStart"
                            : shouldShowEndDate
                              ? "leave.start"
                              : "leave.when",
                    )}
                    dateValue={dateFrom}
                    onDateChange={handleDateFromChange}
                    dateTestID={
                        TEST_IDS[TEST_IDS.LEAVE_REQUEST_DATE_FROM_FIELD]
                    }
                    minimumDate={dateFromMinimumDate}
                    maximumDate={dateFromMaximumDate}
                    time={
                        absenceMode === "arrives"
                            ? timeToField
                            : absenceMode !== "all"
                              ? timeFromField
                              : undefined
                    }
                />
                {(shouldShowEndDate || shouldShowEndTime) && (
                    <DateTimeRow
                        label={t(
                            shouldShowEndTime
                                ? "leave.partOfDayEnd"
                                : "leave.end",
                        )}
                        dateValue={dateTo}
                        onDateChange={
                            shouldShowEndDate ? handleDateToChange : undefined
                        }
                        datePlaceholder={
                            reason === "illness"
                                ? t("leave.notSet")
                                : isExtracurricular && isRecurring
                                  ? t("leave.endDateOptional")
                                  : undefined
                        }
                        dateTestID={
                            TEST_IDS[TEST_IDS.LEAVE_REQUEST_DATE_TO_FIELD]
                        }
                        minimumDate={dateToMinimumDate}
                        maximumDate={dateToMaximumDate}
                        time={shouldShowEndTime ? timeToField : undefined}
                        timeDisabled={
                            (!dateTo && !isRecurring) ||
                            (!shouldShowEndTime && isRecurring)
                        }
                    />
                )}

                {isExtracurricular ? (
                    <>
                        <SelectRow
                            label={t("ui.repeat")}
                            value={repeat}
                            options={repeatOptions}
                            onChange={handleRepeatChange}
                            testID={
                                TEST_IDS[TEST_IDS.LEAVE_REQUEST_REPEAT_FIELD]
                            }
                        />
                        {repeat === "weekly" && (
                            <DaySelectRow
                                daysOfWeek={daysOfWeek}
                                toggleWeekday={toggleWeekday}
                                testIDPrefix={
                                    TEST_IDS[
                                        TEST_IDS.LEAVE_REQUEST_WEEKDAY_CHIP
                                    ]
                                }
                            />
                        )}
                        {isRecurring && (
                            <DateTimeRow
                                label={
                                    <View className="flex-row items-center gap-2">
                                        <Icon name="repeat" size={16} />
                                        <Label>{t("leave.recurringEnd")}</Label>
                                    </View>
                                }
                                dateValue={dateTo}
                                onDateChange={handleDateToChange}
                                datePlaceholder={t("leave.endDateOptional")}
                                dateTestID={
                                    TEST_IDS[
                                        TEST_IDS
                                            .LEAVE_REQUEST_RECURRING_END_FIELD
                                    ]
                                }
                                minimumDate={dateToMinimumDate}
                                maximumDate={dateToMaximumDate}
                            />
                        )}
                    </>
                ) : null}
            </View>
            <Separator />
            <Accordion type="single" collapsible className="-mt-4">
                <AccordionItem value="additional-information">
                    <AccordionTrigger
                        testID={
                            TEST_IDS[
                                TEST_IDS
                                    .LEAVE_REQUEST_ADDITIONAL_INFORMATION_TOGGLE
                            ]
                        }
                    >
                        <View className="flex-1 flex-row items-center justify-between gap-4">
                            <Label>{t("leave.additionalInformation")}</Label>
                            <Text variant="muted">({t("ui.optional")})</Text>
                        </View>
                    </AccordionTrigger>
                    <AccordionContent className="gap-6">
                        <Textarea
                            className="max-h-[200px]"
                            placeholder={t(
                                "leave.additionalInformationPlaceholder",
                            )}
                            value={additionalInformation}
                            onChangeText={setAdditionalInformation}
                            testID={
                                TEST_IDS[
                                    TEST_IDS
                                        .LEAVE_REQUEST_ADDITIONAL_INFORMATION_FIELD
                                ]
                            }
                        />
                        <View className="gap-2">
                            <Label>{t("leave.attachments.label")}</Label>
                            <AttachmentPicker
                                files={attachments}
                                onChange={setAttachments}
                            />
                        </View>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
            <Button
                onPress={handleSubmit}
                disabled={isLoading || !canSubmit}
                testID={TEST_IDS[TEST_IDS.LEAVE_REQUEST_SUBMIT_BUTTON]}
            >
                <Text className="text-primary-foreground">
                    {isLoading ? t("leave.submitting") : t("leave.submit")}
                </Text>
            </Button>
        </View>
    );
}
